import { z } from 'zod'

const pct = z.number().min(0).max(100)

/** GAME_PLAN §2.7: every hazard is telegraphed for at least 0.5 s. */
const telegraphMs = z.number().int().min(500, 'telegraph must be at least 500 ms (GAME_PLAN §2.7)')

/**
 * Race room flow (T1.6, ARCHITECTURE §5.2, D-018). Start delays, results hold and the reconnect
 * window are wall-clock ms; the rest become ticks.
 */
export const matchBalanceSchema = z.strictObject({
  /** Public room: 4 humans for this long → countdown. */
  startFullMs: z.number().int().min(0),
  /** Public room: 2+ humans for this long → countdown. */
  startMultiMs: z.number().int().min(0),
  /** Public room: the first human has waited this long → countdown with bots. */
  startSoloMs: z.number().int().min(0),
  /** Race ends this long after the first finisher. */
  finishGraceMs: z.number().int().positive(),
  /** Hard cap on racing time. */
  raceCapMs: z.number().int().positive(),
  /** `finished` → dispose (public) after this long. */
  resultsHoldMs: z.number().int().positive(),
  /** A disconnected human with no input this long is driven by a bot (ARCHITECTURE §4.4). */
  botTakeoverMs: z.number().int().positive(),
  /** `allowReconnection` window during racing. */
  reconnectMs: z.number().int().positive(),
})

/** Periodic track hazards (T1.2). Per-instance timing lives in the track segments. */
export const hazardBalanceSchema = z.strictObject({
  bladeStunMs: z.number().int().positive(),
  bladeTipSizePx: z.number().int().positive(),
  spikeStunMs: z.number().int().positive(),
  spikeTelegraphMs: telegraphMs,
  spikeHeightPx: z.number().int().positive(),
  fallingBlockStunMs: z.number().int().positive(),
  fallingBlockTelegraphMs: telegraphMs,
  fallingBlockRestMs: z.number().int().min(0),
  // Sky Meadow hazards (MAP_01 §2): static stakes and stone, the trapdoor cycle
  stakeStunMs: z.number().int().positive(),
  stakeHeightPx: z.number().int().positive(),
  spikedStoneStunMs: z.number().int().positive(),
  spikedStoneSizePx: z.number().int().positive(),
  trapdoorStunMs: z.number().int().positive(),
  trapdoorTelegraphMs: telegraphMs,
  trapdoorSprungMs: z.number().int().positive(),
  trapdoorStakeHeightPx: z.number().int().positive(),
  // Driftroot Lagoon (MAP_02 §2): a barrel released from a gate rolls toward the runner
  barrelSpeedPxPerSec: z.number().int().positive(),
  barrelSizePx: z.number().int().positive(),
  barrelTelegraphMs: telegraphMs,
  barrelStunMs: z.number().int().positive(),
})

/** One bot tier (T1.4, ARCHITECTURE §2.7). Min/max pairs are inclusive ranges drawn from `rng.bots`. */
export const botTierSchema = z
  .strictObject({
    lookaheadPx: z.number().int().positive(),
    reactionMinMs: z.number().int().min(0),
    reactionMaxMs: z.number().int().min(0),
    itemDelayMinMs: z.number().int().min(0),
    itemDelayMaxMs: z.number().int().min(0),
    trapWhenRankAtMost: z.number().int().min(1),
    // Variety (T1.4b, D-031): already in ticks, an early/late offset rather than a duration.
    actEarlyMaxTicks: z.number().int().min(0),
    lateChancePct: z.number().int().min(0).max(100),
    lateMinTicks: z.number().int().min(0),
    lateMaxTicks: z.number().int().min(0),
    // Routes (Sky Meadow): each bot draws how often it takes an upper route; a ledge at least
    // `climbMinPx` higher counts as one; walking off a drop of `dropAvoidPx` or more is trouble.
    upperRouteMinPct: pct,
    upperRouteMaxPct: pct,
    climbMinPx: z.number().int().positive(),
    dropAvoidPx: z.number().int().positive(),
    // Items round 2 (T3.12): fire the Leader Missile only when the leader is this far ahead.
    missileMinLeadMs: z.number().int().min(0),
  })
  .refine((t) => t.upperRouteMinPct <= t.upperRouteMaxPct, {
    path: ['upperRouteMaxPct'],
    message: 'must be at least upperRouteMinPct',
  })
  .refine((t) => t.lateMinTicks <= t.lateMaxTicks, {
    path: ['lateMaxTicks'],
    message: 'must be at least lateMinTicks',
  })
  .refine((t) => t.reactionMinMs <= t.reactionMaxMs, {
    path: ['reactionMaxMs'],
    message: 'must be at least reactionMinMs',
  })
  .refine((t) => t.itemDelayMinMs <= t.itemDelayMaxMs, {
    path: ['itemDelayMaxMs'],
    message: 'must be at least itemDelayMinMs',
  })

/** Raw `balance.json` as authored: units live in the key name. */
export const balanceFileSchema = z.strictObject({
  // Running: one speed for everyone (D-045: no sprint, no slow-down)
  baseSpeedPxPerSec: z.number().positive(),
  // Jumping and falling (D-044: one extra jump in the air)
  gravityPxPerSec2: z.number().positive(),
  jumpVelocityPxPerSec: z.number().positive(),
  doubleJumpVelocityPxPerSec: z.number().positive(),
  jumpReleaseKeepPct: pct,
  maxFallSpeedPxPerSec: z.number().positive(),
  coyoteMs: z.number().int().min(0),
  jumpBufferMs: z.number().int().min(0),
  // Hitbox
  playerWidthPx: z.number().int().positive(),
  playerHeightPx: z.number().int().positive(),
  rampSnapPx: z.number().min(0),
  // Terrain zones and platforms
  mudSpeedPct: pct,
  // Feet on a slow strip: speed × this / 100 (a Boost Jet ignores it, like mud)
  slowStripPct: pct,
  bounceVelocityPxPerSec: z.number().positive(),
  speedStripMs: z.number().int().positive(),
  speedStripSpeedPct: z.number().min(0).max(200),
  crumbleDelayMs: z.number().int().min(0),
  crumbleRespawnMs: z.number().int().positive(),
  // Hit tumble (D-045): no control, this share of base speed, a small upward kick
  tumbleSpeedPct: pct,
  tumblePopVelocityPxPerSec: z.number().min(0),
  // Stun and respawn
  postStunInvulnMs: z.number().int().min(0),
  respawnDelayMs: z.number().int().min(0),
  respawnInvulnMs: z.number().int().min(0),
  // A finished runner stops this far past the finish line (never runs off the track)
  finishRunoutPx: z.number().min(0),
  // Runners line up this far apart at the start, in a seeded order (D-045)
  startSpacingPx: z.number().min(0),
  // Match flow: the 3-2-1 before racing (room countdown, practice countdown)
  countdownMs: z.number().int().positive(),
  match: matchBalanceSchema,
  hazards: hazardBalanceSchema,
  bots: z.strictObject({ tier1: botTierSchema }),
})

export type BalanceFile = z.infer<typeof balanceFileSchema>
